import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { Point, Points, View } from "../components/SvgCanvas/renderers";
import { generateRandomPoints } from "../utils/svg";
import { store } from "../utils/storage";
import { LOCAL_STORAGE_KEYS } from "../config/storage";
import type { ModeKey } from "../config/modes";

export type CanvasState = {
	mode: ModeKey;
	view: View;
	points: Points;
	maxEdgeLength: number;
	gradient: {
		startColor: string;
		endColor: string;
	};
	imageFile: File | null;
	history: {
		past: Points[];
		present: Points;
		future: Points[];
	};
	selectedPoints: number[];
	showVertices: boolean;
};

// Attempt to retrieve the program state from local storage.
// If no state is found, return hardcoded defaults.
const getInitialState = (): CanvasState => {
	// Always start in draw mode for immediate usability
	const mode: ModeKey = "draw";
	const view = store.get<View>("view") ?? "gradient";
	const points = store.get<Points>("points") ?? generateRandomPoints();
	const maxEdgeLength = store.get<number>("max-edge-length") ?? 500;
	const showVertices = store.get<boolean>(LOCAL_STORAGE_KEYS.SHOW_VERTICES) ?? true;
	// Default colors must be in hex format: #RRGGBB
	const startColor = store.get<string>("gradient-color-start") ?? "#ff0000";
	const endColor = store.get<string>("gradient-color-end") ?? "#0000ff";

	return {
		mode,
		view,
		points,
		maxEdgeLength,
		gradient: {
			startColor,
			endColor,
		},
		imageFile: null,
		history: {
			past: [],
			present: points,
			future: [],
		},
		selectedPoints: [],
		showVertices,
	};
};

// Save program to local storage
// (changes will persist between page reloads)
const saveToLocalStorage = (state: CanvasState) => {
	// Save program snapshot to local storage
	store.put("points", state.points);
	store.put("view", state.view);
	store.put("mode", state.mode);
	store.put("max-edge-length", state.maxEdgeLength);
	store.put(LOCAL_STORAGE_KEYS.SHOW_VERTICES, state.showVertices);
	store.put("gradient-color-start", state.gradient.startColor);
	store.put("gradient-color-end", state.gradient.endColor);
};

const setMode = (state: CanvasState, action: PayloadAction<ModeKey>) => {
	// Change interaction mode
	state.mode = action.payload;
};

const setView = (state: CanvasState, action: PayloadAction<View>) => {
	// Change rendering view
	state.view = action.payload;
};

const addPoint = (state: CanvasState, action: PayloadAction<Point>) => {
	pushToHistory(state);
	state.points.push(action.payload);
};

const erasePoints = (
	state: CanvasState,
	action: PayloadAction<{ x: number; y: number; radius: number }>,
) => {
	const { x, y, radius } = action.payload;
	const radiusSquared = radius * radius;

	const newPoints = state.points.filter((point) => {
		const dx = point[0] - x;
		const dy = point[1] - y;
		const distanceSquared = dx * dx + dy * dy;
		return distanceSquared > radiusSquared + 0.0001;
	});

	if (newPoints.length !== state.points.length) {
		pushToHistory(state);
		state.points = newPoints;
	}
};

const clearPoints = (state: CanvasState) => {
	if (state.points.length > 0) {
		pushToHistory(state);
		state.points = [];
	}
};

const randomize = (state: CanvasState) => {
	pushToHistory(state);
	state.points = generateRandomPoints();
};

const setPoints = (state: CanvasState, action: PayloadAction<Points>) => {
	pushToHistory(state);
	state.points = action.payload;
	state.history.present = [...action.payload];
};

const setMaxEdgeLength = (
	state: CanvasState,
	action: PayloadAction<number>,
) => {
	// Setting maximum edge length will cutoff long edges,
	// adding white space between points.
	state.maxEdgeLength = action.payload;
};

// GRADIENT

const setGradientStartColor = (
	state: CanvasState,
	action: PayloadAction<string>,
) => {
	state.gradient.startColor = action.payload;
};

const setGradientEndColor = (
	state: CanvasState,
	action: PayloadAction<string>,
) => {
	state.gradient.endColor = action.payload;
};

// UNDO/REDO SYSTEM

const pushToHistory = (state: CanvasState) => {
	state.history.past.push(state.history.present);
	state.history.present = [...state.points];
	state.history.future = [];

	if (state.history.past.length > 50) {
		state.history.past.shift();
	}
};

const undo = (state: CanvasState) => {
	if (state.history.past.length === 0) return;

	state.history.future.unshift(state.history.present);
	state.history.present = state.history.past.pop()!;
	state.points = [...state.history.present];
};

const redo = (state: CanvasState) => {
	if (state.history.future.length === 0) return;

	state.history.past.push(state.history.present);
	state.history.present = state.history.future.shift()!;
	state.points = [...state.history.present];
};

// POINT SELECTION

const selectPoint = (state: CanvasState, action: PayloadAction<number>) => {
	const pointIndex = action.payload;
	if (!state.selectedPoints.includes(pointIndex)) {
		state.selectedPoints.push(pointIndex);
	}
};

const deselectPoint = (state: CanvasState, action: PayloadAction<number>) => {
	const pointIndex = action.payload;
	state.selectedPoints = state.selectedPoints.filter((i) => i !== pointIndex);
};

const togglePointSelection = (
	state: CanvasState,
	action: PayloadAction<number>,
) => {
	const pointIndex = action.payload;
	if (state.selectedPoints.includes(pointIndex)) {
		state.selectedPoints = state.selectedPoints.filter(
			(i) => i !== pointIndex,
		);
	} else {
		state.selectedPoints.push(pointIndex);
	}
};

const clearSelection = (state: CanvasState) => {
	state.selectedPoints = [];
};

const deleteSelectedPoints = (state: CanvasState) => {
	if (state.selectedPoints.length > 0) {
		pushToHistory(state);
		const indicesToDelete = [...state.selectedPoints].sort((a, b) => b - a);
		indicesToDelete.forEach((index) => {
			state.points.splice(index, 1);
		});
		state.selectedPoints = [];
	}
};

const toggleShowVertices = (state: CanvasState) => {
	state.showVertices = !state.showVertices;
};

const canvasSlice = createSlice({
	name: "canvas",
	initialState: getInitialState(),
	reducers: {
		addPoint,
		clearPoints,
		clearSelection,
		deleteSelectedPoints,
		deselectPoint,
		erasePoints,
		randomize,
		saveToLocalStorage,
		selectPoint,
		setGradientEndColor,
		setGradientStartColor,
		setMaxEdgeLength,
		setMode,
		setPoints,
		setView,
		togglePointSelection,
		toggleShowVertices,
		undo,
		redo,
	},
});

export const { ...canvasActions } = canvasSlice.actions;
export default canvasSlice.reducer;
